import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { L10n } from '../../ts/L10n/L10n';
import { BackgroundJobTaskType } from './BackgroundJobInfo';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { IBackgroundJob } from './IBackgroundJob';
import { ISchedule } from '../../schedule/types/ISchedule';

export interface IBackgroundJobExtendedInfo {
  luid: string; //luid of the background job
  taskType: keyof typeof BackgroundJobTaskType | undefined; // see BackgroundJobTaskType for more info
  projectName: string | undefined; // the project name that the content belongs to
  schedule: ISchedule; // the schedule the task is related to
  scheduleName: string | undefined; // the schedule name that the task is related to
  contentName: string | undefined; // the name of the content
  associatedUserName: string | undefined; // the user name that owns the content
  siteName: string | undefined; // the name of the site
  jobCreatorName: string | undefined; // the user name that created the job
}

export function getBackgroundJobExtendedInfoScheduleRow(extendedInfo: IBackgroundJobExtendedInfo): string | undefined {
  // If available return the name
  if (extendedInfo.scheduleName) {
    return extendedInfo.scheduleName;
  }

  // Otherwise if possible compute a description
  // Note: The schedule object will contain a name, but if it isn't specified by the extended info itself,
  // it's probably just a LUID
  if (extendedInfo.schedule) {
    return IScheduleInfo.getScheduleDescription(extendedInfo.schedule as IScheduleInfo);
  }

  // Extended info does not contain name or information required to derive description
  //  => Give up
  return;
}

export function getBackgroundJobExtendedInfoDialog(backgroundJob: IBackgroundJob, extendedInfo: IBackgroundJobExtendedInfo, isServerPage: boolean): string[] {
  const result = [];

  // LUID
  const luid = Messages.backgroundJobsExtendedInfoLuid({
    LUID: extendedInfo.luid,
  });
  result.push(luid);

  // Project
  if (extendedInfo.projectName) {
    const project = Messages.backgroundJobsExtendedInfoProject({
      PROJECT_NAME: extendedInfo.projectName,
    });
    result.push(project);
  }

  // Schedule
  const schedule = getBackgroundJobExtendedInfoScheduleRow(extendedInfo);
  if (schedule) {
    const scheduleRow = Messages.backgroundJobsExtendedInfoSchedule({
      SCHEDULE_NAME: schedule,
    });
    result.push(scheduleRow);
  }

  // Content Name
  if (extendedInfo.contentName) {
    const contentName = Messages.backgroundJobsExtendedInfoContent({
      CONTENT_NAME: extendedInfo.contentName,
    });
    result.push(contentName);
  }

  // Associated User
  if (extendedInfo.associatedUserName) {
    const associatedUser = Messages.backgroundJobsExtendedInfoContentOwner({
      OWNER_NAME: extendedInfo.associatedUserName,
    });
    result.push(associatedUser);
  }

  // Job Creator
  if (extendedInfo.jobCreatorName) {
    const jobCreator = Messages.backgroundJobsExtendedInfoJobCreator({
      CREATOR_NAME: extendedInfo.jobCreatorName,
    });
    result.push(jobCreator);
  }

  if (FeatureFlags.jobManagement194UIEnabled && backgroundJob.taskLastSuccessTime) {
    const lastSucceededAt = Messages.backgroundJobsExtendedInfoLastSuccessfulRun({
      LAST_SUCCEESSFUL_RUN: L10n.localizedDateTime(backgroundJob.taskLastSuccessTime)
    });
    result.push(lastSucceededAt);
  }

  // Site ID
  if (isServerPage && extendedInfo.siteName) {
    const site = Messages.backgroundJobsExtendedInfoSite({
      SITE_NAME: extendedInfo.siteName,
    });
    result.push(site);
  }

  return result;
}
